import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Eye, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button.js';
import { Badge } from '@/components/ui/Badge.js';
import { ConfirmDialog } from '@/components/ui/Modal.js';
import { cn } from '@/lib/cn.js';
import { autosaveLabel, type AutosaveState } from './useAutosave.js';
import type { EditLockState } from './useEditLock.js';

export type ContentFormLayoutProps = {
  title: string;
  backTo: string;
  backLabel: string;
  /** Yangi yozuvda nashr tugmasi ko'rsatilmaydi — avval saqlanishi kerak. */
  isNew: boolean;
  isPublished: boolean;
  saving: boolean;
  publishing?: boolean;
  canPublish: boolean;
  autosave: AutosaveState;
  lock: EditLockState;
  unsaved: { blocked: boolean; proceed: () => void; cancel: () => void };
  onSave: () => void;
  onTogglePublish?: () => void;
  onPreview?: () => void;
  children: ReactNode;
  /** O'ng tomondagi qo'shimcha tugmalar. */
  actions?: ReactNode;
};

/**
 * Kontent tahrirlash ekranining umumiy karkasi.
 *
 * Yuqori panel 9 ta bo'limda bir xil: orqaga, sarlavha, nashr holati,
 * avtosaqlash ko'rsatkichi, «Ko'rish», «Saqlash». Har bir bo'limda qayta
 * yozilsa, birortasida avtosaqlash ko'rsatkichi yoki qulf ogohlantirishi
 * tushib qolardi.
 */
export function ContentFormLayout({
  title,
  backTo,
  backLabel,
  isNew,
  isPublished,
  saving,
  publishing = false,
  canPublish,
  autosave,
  lock,
  unsaved,
  onSave,
  onTogglePublish,
  onPreview,
  children,
  actions,
}: ContentFormLayoutProps) {
  const autosaveText = autosaveLabel(autosave);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/*
        Yuqori panel — MAKETDAN (`D3`, TAHRIRLASH): orqaga havolasi,
        ajratuvchi chiziq, sarlavha, nashr belgisi; o'ng chekkada
        avtosaqlash holati va uchta tugma.
      */}
      <header className="sticky top-0 z-20 border-b border-n-200 bg-white">
        <div className="flex flex-wrap items-center gap-x-[18px] gap-y-3 px-4 py-3.5 sm:px-6">
          <Link
            to={backTo}
            className="inline-flex items-center gap-2 text-[13.5px] font-semibold text-brand-600"
          >
            <ArrowLeft className="size-[15px]" aria-hidden />
            {backLabel}
          </Link>

          <span aria-hidden className="hidden h-[22px] w-px bg-n-200 sm:block" />

          <div className="min-w-0 flex-1">
            <h1 className="truncate text-base font-semibold text-n-900">{title}</h1>
          </div>

          {!isNew && (
            <Badge tone={isPublished ? 'success' : 'neutral'}>
              {isPublished ? 'Nashr qilingan' : 'Qoralama'}
            </Badge>
          )}

          {autosaveText && (
            <span
              role="status"
              className={cn(
                'text-[12.5px]',
                autosave.status === 'error' ? 'text-error-600' : 'text-n-600',
              )}
            >
              {autosaveText}
            </span>
          )}

          <div className="flex items-center gap-2.5">
            {actions}

            {onPreview && !isNew && (
              <Button variant="outline" icon={<Eye className="size-4" />} onClick={onPreview}>
                Ko‘rish
              </Button>
            )}

            {/*
              Maketda «Saqlash» — chegarali, «Nashr qilish» esa AKSENT:
              nashr qilish qaytarib bo'lmaydigan qadam va u ajralib
              turishi kerak.
            */}
            <Button
              variant="outline"
              icon={<Save className="size-4" />}
              loading={saving}
              onClick={onSave}
              className="border-brand-600 text-brand-600 hover:bg-brand-50"
            >
              Saqlash
            </Button>

            {onTogglePublish && !isNew && canPublish && (
              <Button
                variant={isPublished ? 'outline' : 'secondary'}
                loading={publishing}
                onClick={onTogglePublish}
              >
                {isPublished ? 'Nashrdan olish' : 'Nashr qilish'}
              </Button>
            )}
          </div>
        </div>

        {lock.status === 'taken' && <LockBanner lock={lock} />}
      </header>

      <div className="min-h-0 flex-1">{children}</div>

      <ConfirmDialog
        open={unsaved.blocked}
        title="Saqlanmagan o‘zgarishlar"
        message="Bu sahifada saqlanmagan o‘zgarishlar bor. Chiqib ketsangiz ular yo‘qoladi."
        confirmLabel="Baribir chiqish"
        danger
        onConfirm={unsaved.proceed}
        onCancel={unsaved.cancel}
      />
    </div>
  );
}

/**
 * «Bu sahifani hozir X tahrirlamoqda».
 *
 * Tahrirlash BLOKLANMAYDI — server ham saqlashni to'xtatmaydi. Xodim
 * ogohlantirishni ko'rib o'zi qaror qiladi: kutadimi yoki davom etadimi.
 */
function LockBanner({ lock }: { lock: EditLockState }) {
  return (
    <div
      role="alert"
      className="flex flex-wrap items-center gap-3 border-t border-warning-200 bg-warning-50 px-4 py-2 text-sm text-warning-600 sm:px-6"
    >
      <AlertTriangle className="size-4 shrink-0" aria-hidden />
      <span className="flex-1">
        Bu sahifani hozir <strong>{lock.holder?.userName}</strong> tahrirlamoqda. Ikkalangiz ham
        saqlasangiz, keyingi saqlash oldingisini almashtiradi.
      </span>
      <Button size="sm" variant="outline" onClick={() => void lock.takeOver()}>
        Baribir tahrirlash
      </Button>
      <Button size="sm" variant="ghost" onClick={lock.dismiss}>
        Yopish
      </Button>
    </div>
  );
}
